<script setup>
import { isEqual } from 'lodash-es';
import { watch } from 'vue';
import { useModal } from 'vue-final-modal';

import DashboardTaskListModal from '~/dashboard/components/dashboard-task-list-modal.vue';
import { ruleToFilterFormatter } from '~/dashboard/components/filters/composables/tasks-filters.js';

import GraphComponent from '~/dashboard/components/widgets/graph-widgets/graph-component.vue';

import { useDashboardTasksStore } from '~/dashboard/store/dashboard-tasks.store.js';

const props = defineProps({
  data: {
    type: Object,
  },
  id: {
    type: String,
  },
  // eslint-disable-next-line vue/prop-name-casing
  is_mobile_view: {
    type: Boolean,
    default: false,
  },
  activeSelectedRange: {
    type: Array,
    default: () => [],
  },
});

const dashboard_tasks_store = useDashboardTasksStore();

const $services = inject('$services');
const graph_data = ref(null);
const loading = ref(false);
const payload = ref(null);

const chart_config = computed(() => {
  return props.data?.data?.chart_config;
});
const chart_type = computed(() => {
  let type;
  switch (props.data?.data?.type) {
    case 'vertical_graph':
      type = chart_config.value?.chart_display_mode === 'Unstacked' ? 'mscolumn2d' : 'stackedcolumn2d';
      break;
    case 'horizontal_bar':
      type = chart_config.value?.chart_display_mode === 'Unstacked' ? 'msbar2d' : 'stackedbar2d';
      break;
    case 'donut':
      type = 'doughnut2d';
      break;
  }

  return type;
});

const tasks_list_modal = useModal({
  component: DashboardTaskListModal,
});

const graph_config = computed(() => ({
  chart_name: props.data.data.name,
  dimensions: {
    x: props.data.x,
    y: props.data.y,
    h: props.data.h,
  },
  renderAt: `chart-${props.id}`,
  type: chart_type.value || '',
  dataSource: {
    chart: {
      xAxisName: chart_config.value?.x_label || payload?.value?.x?.label || '',
      yAxisName: chart_config.value?.y_label || payload?.value?.y?.label || '',
      subCaption: chart_config.value?.description
        || props.data.data.subCaption || '',
    },
    dashboard_index: props.data.i,
    chart_type: props.data.data.type,
    ...graph_data.value,
  },
  events: {
    dataPlotClick: (e) => {
      window.getTasks = (query) => {
        const count = e?.data.value || 0;
        const popup_query = JSON.parse(atob(query));
        tasks_list_modal.patchOptions({
          attrs: {
            id: props.id,
            name: `${props.data?.data?.name}`,
            count,
            payload: payload.value,
            popup_query,
            onClose() {
              tasks_list_modal.close();
            },
          },
        });
        tasks_list_modal.open();
      };
    },
  },
}));

async function getReports() {
  graph_data.value = null;
  loading.value = true;
  payload.value = dashboard_tasks_store.parse_tasks_form_to_server_format(props.data.data);

  const { filters, report_filters } = ruleToFilterFormatter(payload.value?.filter_rules?.length ? payload.value.filter_rules : []);
  payload.value.filters = {
    organization: payload.value.filters?.organization,
    asset_uid: payload.value.filters?.asset_uid,
    sub_tasks: payload.value.filters?.sub_tasks,
    ...filters,
  };
  payload.value.properties.report_filters = report_filters;

  try {
    const { data } = await $services.tasks.get_graph({ body: payload.value });
    graph_data.value = data;
    loading.value = false;
  }
  catch {
    loading.value = false;
  }
}

if (props.id === 'preview') {
  watch(() => props.data, (new_val, old_val) => {
    if (new_val && !isEqual(new_val, old_val)) {
      setTimeout(() => {
        getReports();
      }, 250);
    }
  }, { immediate: true, deep: true });
}

if (props.id !== 'preview') {
  watch(() => props.data.data, (new_val, old_val) => {
    if (new_val && !isEqual(new_val, old_val)) {
      setTimeout(() => {
        getReports();
      }, 250);
    }
  }, { immediate: true, deep: true });
}

watch(() => props.activeSelectedRange, (new_val, old_val) => {
  if (!isEqual(new_val, old_val) && (props?.id !== 'preview')) {
    getReports();
  }
});
</script>

<template>
  <div class="w-full h-full">
    <div v-if="$slots['header-title'] || $slots['header-actions']" class="widget-header group">
      <slot name="header-title" />
      <slot name="header-actions" />
    </div>
    <hawk-loader v-if="loading" />
    <GraphComponent v-else-if="graph_config?.renderAt" :id="props.id" :configuration="graph_config" :chart_config="chart_config" :group_key="chart_type !== 'doughnut2d' && props.data?.data?.breakdown?.key" />
  </div>
</template>
